import React, { useState, useContext, useEffect } from 'react';
import {
  BankOutlined,
  HomeOutlined,
  StarOutlined,
  EnvironmentOutlined,
  WarningOutlined,
  CameraOutlined,
} from '@ant-design/icons';
import { AuthContext } from '../context/AuthContext';
import axios from 'axios';
import dayjs from "dayjs";

const user_avatar = "https://api.dicebear.com/7.x/thumbs/svg?seed=user";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

  * { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    font-family: 'DM Sans', sans-serif;
    background: #f0f2f5;
    min-height: 100vh;
    padding: 32px 16px;
  }

  .log-wrapper {
    max-width: 780px;
    margin: 0 auto;
  }

  .log-header {
    margin-bottom: 32px;
  }

  .log-header h2 {
    font-size: 22px;
    font-weight: 600;
    color: #1a1a2e;
    letter-spacing: -0.3px;
  }

  .log-header p {
    font-size: 13px;
    color: #888;
    margin-top: 4px;
    font-family: 'DM Mono', monospace;
  }

  .timeline {
    position: relative;
    padding-left: 28px;
  }

  .timeline::before {
    content: '';
    position: absolute;
    left: 10px;
    top: 12px;
    bottom: 12px;
    width: 2px;
    background: linear-gradient(to bottom, #0e497b, #0e497b, #0e497b);
    border-radius: 2px;
  }

  .timeline-item {
    position: relative;
    margin-bottom: 20px;
    animation: fadeUp 0.4s ease both;
  }

  .timeline-item:nth-child(1) { animation-delay: 0.05s; }
  .timeline-item:nth-child(2) { animation-delay: 0.15s; }
  .timeline-item:nth-child(3) { animation-delay: 0.25s; }

  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .dot {
    position: absolute;
    left: -22px;
    top: 18px;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 2.5px solid white;
    box-shadow: 0 0 0 2px currentColor;
    z-index: 1;
  }

  .dot.check { color: #0e497b; background: #0e497b; }
  .dot.punch { color: #0e497b; background: #0e497b; }

  .card {
    background: white;
    border-radius: 14px;
    padding: 18px 20px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 4px 16px rgba(0,0,0,0.04);
    border: 1px solid #f0f0f0;
    transition: box-shadow 0.2s;
  }

  .card:hover {
    box-shadow: 0 4px 20px rgba(0,0,0,0.09);
  }

  .card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
  }

  .location-badge {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #1a1a2e;
  }

  .location-icon {
    width: 26px;
    height: 26px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .type-tag {
    font-size: 11px;
    font-weight: 500;
    font-family: 'DM Mono', monospace;
    padding: 3px 10px;
    border-radius: 20px;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }

  .type-tag.check { background: #eef0ff; color: #0e497b; }
  .type-tag.punch { background: #f3eeff; color: #0e497b; }

  .times-row {
    display: flex;
    gap: 12px;
    margin-bottom: 14px;
  }

  .time-block {
    flex: 1;
    background: #ffffff;
    border-radius: 10px;
    padding: 10px 5px;
    border: none;
  }

  .time-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #aaa;
    margin-bottom: 4px;
  }

  .time-value {
    font-size: 14px;
    font-weight: 500;
    color: #1a1a2e;
    font-family: 'DM Mono', monospace;
  }

  .time-value.missing {
    color: #f59e0b;
    font-size: 12px;
    display: flex;
    align-items: center;
    gap: 5px;
  }

  .photos-section {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .photos-section-label {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    color: #aaa;
    margin-right: 2px;
  }

  .photo-block {
    position: relative;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #e8e8e8;
    width: 100px;
    height: 100px;
    flex-shrink: 0;
  }

  .photo-block img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .photo-label {
    position: absolute;
    bottom: 3px;
    left: 3px;
    font-size: 8px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    background: rgba(0,0,0,0.5);
    color: white;
    padding: 1px 5px;
    border-radius: 20px;
    line-height: 1.6;
  }

  .no-photo {
    width: 48px;
    height: 48px;
    background: #fafafa;
    border: 1px dashed #ddd;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    color: #bbb;
    font-size: 9px;
    flex-shrink: 0;
  }

  .punch-divider {
    height: 1px;
    background: #f0f0f0;
    margin: 12px 0;
  }
`;

function getEntryType(punch) {
  return punch?.PunchIn || punch?.PunchOut ? 'punch' : 'check';
}

function getInTime(punch, type) {
  return type === "punch" ? punch?.PunchIn : punch?.CheckIn;
}

function getOutTime(punch, type) {
  return type === "punch" ? punch?.PunchOut : punch?.CheckOut;
}

const locationConfig = {
  "Head Office": { Icon: BankOutlined, bg: "#eef0ff", color: "#0e497b" },
  "Crown Residencies": { Icon: HomeOutlined, bg: "#f3eeff", color: "#0e497b" },
  "Enchanted": { Icon: StarOutlined, bg: "#fff7e6", color: "#0e497b" },
};


export default function ImproperLog({ record, employee }) {
  const [attendanceData, setAttendanceData] = useState([]);
  const { authData, backUrl } = useContext(AuthContext);
  const [showLead, setShowLead] = useState(false);
  const [selectedLeadDetails, setSelectedLeadDetails] = useState(null);
  // Stores resolved location names keyed by "lat,lng"
  const [locationNames, setLocationNames] = useState({});

  const today = dayjs().format("YYYY-MM-DD");

  console.log("RECORD  :", record)
  console.log("Employee :", employee)

  const fetchData = async () => {
    try {
      const params = new URLSearchParams({ date: record.Date });
      if (employee?.id) params.append('employee_id', employee.id);

      const res = await axios.get(
        `${backUrl}/api/hr/attendance-comparsion/?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${authData?.access}`,
          },
        }
      );
      setAttendanceData(res.data.results || []);
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      setAttendanceData([]);
    }
  };

  // Reverse-geocode coordinates to a human-readable location name
  const fetchLocationName = async (lat, lng) => {
    const key = `${lat},${lng}`;
    if (locationNames[key]) return; // already fetched
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&accept-language=en`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await res.json();
      // Use suburb/neighbourhood/town for a short name, fallback to full display_name
      const addr = data.address || {};
      const shortName =
        addr.suburb ||
        addr.neighbourhood ||
        addr.village ||
        addr.town ||
        addr.city ||
        addr.county ||
        data.display_name ||
        `${lat}, ${lng}`;
      setLocationNames((prev) => ({ ...prev, [key]: shortName }));
    } catch {
      setLocationNames((prev) => ({ ...prev, [key]: `${lat}, ${lng}` }));
    }
  };

  useEffect(() => {
    fetchData()
  }, [])

  // Fetch location names for all auto-checkout entries after data loads
  useEffect(() => {
    attendanceData.forEach((entry) => {
      const lat = entry?.sales_checkout_latitude;
      const lng = entry?.sales_checkout_longitude;
      if (lat && lng) fetchLocationName(lat, lng);
    });
  }, [attendanceData]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <style>{styles}</style>
      <div className="log-wrapper">
        <div className="timeline">
          {attendanceData.map((entry, entryIdx) => {
            const punchDetails = entry?.punch_details || [];
            const sales_remarks = entry?.sales_checkout_remarks || null;

            return punchDetails.map((punch, punchIdx) => {
              const type = getEntryType(punch);
              const inTime = getInTime(punch, type);
              const outTime = getOutTime(punch, type);
              const locConf = locationConfig[entry.WorkLocation] || { Icon: EnvironmentOutlined, bg: "#f0f0f0", color: "#888" };
              const { Icon } = locConf;
              const autoCheckoutRemarks = entry?.sales_checkout_remarks || null;
              const autoLat = entry?.sales_checkout_latitude || null;
              const autoLng = entry?.sales_checkout_longitude || null;

              return (
                <div className="timeline-item" key={`${entryIdx}-${punchIdx}`}>
                  <div className={`dot ${type}`} />
                  <div className="card">
                    <div className="card-top">
                      <div className="location-badge">
                        {punch.check_in_work_site && punch.check_out_work_site ? (
                          `${punch.check_in_work_site} - ${punch.check_out_work_site}`
                        ) : punch.check_in_work_site ? (
                          punch.check_in_work_site
                        ) : punch.check_out_work_site ? (
                          punch.check_out_work_site
                        ) : entry.WorkLocation}
                      </div>
                    </div>

                    {inTime && (
                      <div className="times-row">
                        <div className="time-block">
                          <div className="time-label">{type === 'check' ? 'Check In' : 'Punch In'}</div>
                          {inTime
                            ? <div className="time-value">{inTime}</div>
                            : <div className="time-value missing">No record</div>
                          }
                        </div>
                        <div className="time-block">
                          <div className="time-label">{type === 'check' ? 'Check Out' : 'Punch Out'}</div>
                          {outTime
                            ? <div className="time-value">{outTime}</div>
                            : <div className="time-value missing">No record</div>
                          }
                        </div>
                      </div>
                    )}

                    {(() => {
                      const raw = punch.lead_data || punch.lead_details || punch.external_lead_details;
                      if (!raw) return false;
                      try {
                        const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                        if (Array.isArray(parsed)) return parsed.length > 0;
                        if (typeof parsed === "object" && parsed !== null) return Object.keys(parsed).length > 0;
                        return !!parsed;
                      } catch (e) {
                        return typeof raw === "string" && raw.trim() !== "" && raw !== "[]" && raw !== "{}";
                      }
                    })() && (
                        <div style={{ marginBottom: 14 }}>
                          <a
                            onClick={() => {
                              const raw = punch.lead_data || punch.lead_details || punch.external_lead_details;
                              let parsed = raw;
                              if (typeof raw === "string") { try { parsed = JSON.parse(raw); } catch { } }
                              const arr = Array.isArray(parsed) ? parsed : [parsed];
                              setSelectedLeadDetails(arr);
                              setShowLead(true);
                            }}
                            style={{
                              color: "#0e497b",
                              fontSize: "12px",
                              fontWeight: 600,
                              textDecoration: "underline",
                              cursor: "pointer"
                            }}
                          >
                            View Visit Lead Details
                          </a>
                        </div>
                      )}

                    {type === 'check' && (
                      <>
                        <div className="photos-section">
                          <span className="photos-section-label">Photos</span>

                          {punch.check_in_photo_url ? (
                            <div className="photo-block">
                              <img src={punch.check_in_photo_url} alt="Check-in" />
                              <span className="photo-label">In</span>
                            </div>
                          ) : (
                            <div className="no-photo">
                              <CameraOutlined style={{ fontSize: 13 }} />
                              <span>In</span>
                            </div>
                          )}

                          {punch.check_out_photo_url ? (
                            <div className="photo-block">
                              <img src={punch.check_out_photo_url} alt="Check-out" />
                              <span className="photo-label">Out</span>
                            </div>
                          ) : (
                            <div className="no-photo">
                              <CameraOutlined style={{ fontSize: 13 }} />
                              <span>Out</span>
                            </div>
                          )}
                        </div>

                        {/* Auto-Checkout Alert — only rendered when auto-checkout occurred */}
                        {autoCheckoutRemarks && (
                          <div style={{
                            marginTop: 12,
                            padding: "8px 12px",
                            background: "#fff7e6",
                            border: "1px solid #ffa940",
                            borderRadius: 8,
                            fontSize: 12,
                            color: "#d46b08",
                            fontWeight: 500,
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                          }}>
                            <span>⚠️ {autoCheckoutRemarks}</span>
                            {autoLat && autoLng && (
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${autoLat},${autoLng}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#1677ff", fontSize: 11, marginTop: 2, display: "flex", alignItems: "center", gap: 4 }}
                              >
                                <EnvironmentOutlined style={{ marginRight: 3 }} />
                                {locationNames[`${autoLat},${autoLng}`] || "Fetching location..."}
                              </a>
                            )}
                          </div>
                        )}
                      </>
                    )}


                  </div>
                </div>
              );
            });
          })}

        </div>
      </div>

      {showLead && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '600px',
            maxHeight: '80vh', overflowY: 'auto', padding: '24px', position: 'relative'
          }}>
            <button
              onClick={() => setShowLead(false)}
              style={{
                position: 'absolute', top: '16px', right: '16px', border: 'none',
                background: 'none', cursor: 'pointer', fontSize: '20px', color: '#888'
              }}
            >
              ×
            </button>
            <h3 style={{ marginBottom: '20px', color: '#1a1a2e' }}>Lead Details</h3>
            {selectedLeadDetails && selectedLeadDetails.map((lead, index) => (
              <div key={index} style={{
                marginBottom: '16px', padding: '16px', borderRadius: '8px',
                border: '1px solid #f0f0f0', backgroundColor: '#fafafa'
              }}>
                {selectedLeadDetails.length > 1 && <h4 style={{ marginBottom: '12px' }}>Lead {index + 1}</h4>}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', rowGap: '10px', fontSize: '14px' }}>
                  <div style={{ color: '#888' }}>Lead No:</div>
                  <div style={{ fontWeight: 600 }}>{lead.lead_no || lead.lead_id || "-"}</div>
                  <div style={{ color: '#888' }}>Customer:</div>
                  <div>{lead.customer_name || lead.name || "-"}</div>
                  <div style={{ color: '#888' }}>Email:</div>
                  <div>{lead.email || lead.customer_email || "-"}</div>
                  <div style={{ color: '#888' }}>Phone:</div>
                  <div>{lead.number || lead.phone || "-"}</div>
                  <div style={{ color: '#888' }}>Project:</div>
                  <div>{lead.project_name || lead.project_id || (lead.project && lead.project.title) || "-"}</div>
                  <div style={{ color: '#888' }}>Status:</div>
                  <div><span style={{
                    padding: '2px 8px', borderRadius: '4px', fontSize: '12px',
                    backgroundColor: '#eef0ff', color: '#0e497b', fontWeight: 500
                  }}>
                    {lead.status?.title || lead.status || "-"}
                  </span></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}